import { createContext, useContext } from 'react';
import type { User } from 'firebase/auth';

import type { AuthClaims } from '../../services/firebase/auth';
import type { NpcConfig, UserNpcConfig } from '../../types/npc';
import type { SceneDocument } from '../../types/scene';
import type { AvatarFormValues, UserProfile } from '../../types/user';

export interface AppContextValue {
  authUser: User | null;
  authClaims: AuthClaims;
  isAuthBypass: boolean;
  profile: UserProfile | null;
  showAvatarEditor: boolean;
  galleryScenes: SceneDocument[];
  isGalleryLoading: boolean;
  sceneGalleryError: string | null;
  npcConfigs: Record<string, NpcConfig>;
  conversationCount: number;
  availableSceneTemplates: SceneDocument[];

  refreshSceneGallery: (uid: string | null) => Promise<void>;
  refreshNpcConfigs: (uid: string | null, scene: SceneDocument | null) => Promise<void>;
  saveAvatar: (values: AvatarFormValues) => Promise<void>;
  upsertGalleryScene: (scene: SceneDocument) => void;
  setConversationCount: (value: number) => void;
  setProfile: (profile: UserProfile | null) => void;
  applyOptimisticUserNpcToScene: (scene: SceneDocument, userNpc: UserNpcConfig) => void;
  deleteSceneFromGallery: (scene: SceneDocument) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppContextProvider = AppContext.Provider;

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useAppContext must be used within AppLayout');
  }
  return ctx;
}
